import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddRounded,
  CheckRounded,
  CloseRounded,
  DeleteOutlineRounded,
  EditOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingLabel,
  DocumentCraftingLabelColor,
  DocumentCraftingLabelScope,
  LABEL_COLORS,
} from "../../../Models/documentCrafting";
import {
  useCreateDocumentCraftingLabel,
  useDeleteDocumentCraftingLabel,
  useDocumentCraftingLabels,
  useUpdateDocumentCraftingLabel,
} from "../../../Query/useDocumentCrafting";
import ConfirmDialog from "../Common/ConfirmDialog";
import { ErrorState, LoadingState, Panel } from "../Common/DocumentCraftingUi";
import { LabelChip, LabelColorDot } from "../Common/LabelChips";
import { LABEL_NAME_MAX } from "../Detail/LabelPicker";
import LabelColorSwatches from "./LabelColorSwatches";

export type LabelNameError = "" | "nameRequired" | "nameTooLong" | "nameTaken";

/** Name check of the label forms: required, at most 40 characters, unique
 * per scope ignoring case (the server enforces the same, 409). */
export const labelNameError = (
  name: string,
  existing: DocumentCraftingLabel[],
  exceptId?: number,
): LabelNameError => {
  const trimmed = name.trim();
  if (!trimmed) return "nameRequired";
  if (trimmed.length > LABEL_NAME_MAX) return "nameTooLong";
  const taken = existing.some(
    (label) =>
      label.id !== exceptId &&
      label.name.trim().toLowerCase() === trimmed.toLowerCase(),
  );
  return taken ? "nameTaken" : "";
};

const headCellSx = {
  fontSize: 12,
  fontWeight: 700,
  color: color.gray500,
  whiteSpace: "nowrap",
} as const;

interface RowProps {
  label: DocumentCraftingLabel;
  siblings: DocumentCraftingLabel[];
  editable: boolean;
  project: boolean;
}

const LabelRow: React.FC<RowProps> = ({ label, siblings, editable, project }) => {
  const { t } = useTranslation("documentCrafting");
  const update = useUpdateDocumentCraftingLabel();
  const remove = useDeleteDocumentCraftingLabel();
  const [name, setName] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const pending = update.isPending || remove.isPending;
  const nameError = name === null ? "" : labelNameError(name, siblings, label.id);

  const patch = (
    data: { name?: string; color?: DocumentCraftingLabelColor; archived?: boolean },
    onDone?: () => void,
  ) => update.mutate({ labelId: label.id, data }, { onSuccess: onDone });

  const saveName = () => {
    if (name === null || nameError) return;
    if (name.trim() === label.name) setName(null);
    else patch({ name: name.trim() }, () => setName(null));
  };

  const usage = label.usage_count;
  const deleteText = !project
    ? t("settings.labels.deleteTextPersonal")
    : usage === undefined
      ? t("settings.labels.deleteTextUnknown")
      : usage > 0
        ? t("settings.labels.deleteTextUsed", { count: usage })
        : t("settings.labels.deleteTextUnused");

  return (
    <TableRow sx={{ opacity: label.archived ? 0.75 : 1 }}>
      <TableCell sx={{ minWidth: 220 }}>
        {name !== null ? (
          <Box
            component="form"
            onSubmit={(event: React.FormEvent) => {
              event.preventDefault();
              saveName();
            }}
            sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}
          >
            <TextField
              autoFocus
              size="small"
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") setName(null);
              }}
              error={Boolean(nameError)}
              helperText={
                nameError
                  ? t(`settings.labels.${nameError}`, { max: LABEL_NAME_MAX })
                  : undefined
              }
              disabled={pending}
              inputProps={{
                "aria-label": t("settings.labels.name"),
                maxLength: LABEL_NAME_MAX + 10,
              }}
            />
            <IconButton
              type="submit"
              size="small"
              disabled={Boolean(nameError) || pending}
              aria-label={t("settings.labels.renameSave")}
            >
              <CheckRounded fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setName(null)}
              disabled={pending}
              aria-label={t("common.cancel")}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <LabelChip label={label} />
        )}
      </TableCell>
      <TableCell>
        {editable ? (
          <LabelColorSwatches
            value={label.color}
            onChange={(value) => value !== label.color && patch({ color: value })}
            disabled={pending}
            size={18}
          />
        ) : (
          <LabelColorDot value={label.color} size={14} />
        )}
      </TableCell>
      {project && (
        <TableCell sx={{ whiteSpace: "nowrap", fontSize: 13 }}>
          {usage === undefined
            ? "–"
            : t("settings.labels.usageCount", { count: usage })}
        </TableCell>
      )}
      {project && (
        <TableCell>
          <Switch
            size="small"
            checked={label.archived}
            disabled={!editable || pending}
            onChange={(event) => patch({ archived: event.target.checked })}
            inputProps={{
              "aria-label": t("settings.labels.archiveLabel", { name: label.name }),
            }}
          />
        </TableCell>
      )}
      {editable && (
        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
          <Tooltip title={t("settings.labels.rename")} arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => setName(label.name)}
                disabled={pending || name !== null}
                aria-label={t("settings.labels.renameLabel", { name: label.name })}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("settings.labels.delete")} arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => setConfirmDelete(true)}
                disabled={pending}
                aria-label={t("settings.labels.deleteLabel", { name: label.name })}
              >
                <DeleteOutlineRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <ConfirmDialog
            open={confirmDelete}
            title={t("settings.labels.deleteTitle", { name: label.name })}
            text={deleteText}
            confirmLabel={t("settings.labels.delete")}
            pending={remove.isPending}
            danger
            onConfirm={() =>
              remove.mutate(label.id, { onSuccess: () => setConfirmDelete(false) })
            }
            onClose={() => setConfirmDelete(false)}
          />
        </TableCell>
      )}
    </TableRow>
  );
};

const NewLabelForm: React.FC<{
  scope: DocumentCraftingLabelScope;
  existing: DocumentCraftingLabel[];
}> = ({ scope, existing }) => {
  const { t } = useTranslation("documentCrafting");
  const create = useCreateDocumentCraftingLabel();
  const [name, setName] = useState("");
  const [labelColor, setLabelColor] = useState<DocumentCraftingLabelColor>(
    LABEL_COLORS[existing.length % LABEL_COLORS.length],
  );
  const [touched, setTouched] = useState(false);
  const error = labelNameError(name, existing);
  const shownError = touched || error === "nameTaken" || error === "nameTooLong" ? error : "";

  return (
    <Box
      component="form"
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        setTouched(true);
        if (error) return;
        create.mutate(
          { scope, name: name.trim(), color: labelColor },
          {
            onSuccess: () => {
              setName("");
              setTouched(false);
              setLabelColor(
                LABEL_COLORS[(existing.length + 1) % LABEL_COLORS.length],
              );
            },
          },
        );
      }}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        flexWrap: "wrap",
        mb: 2,
      }}
    >
      <TextField
        size="small"
        label={t("settings.labels.newName")}
        value={name}
        onChange={(event) => setName(event.target.value)}
        error={Boolean(shownError)}
        helperText={
          shownError
            ? t(`settings.labels.${shownError}`, { max: LABEL_NAME_MAX })
            : `${name.trim().length} / ${LABEL_NAME_MAX}`
        }
        disabled={create.isPending}
        sx={{ flex: "1 1 200px", maxWidth: 320 }}
        inputProps={{ maxLength: LABEL_NAME_MAX + 10 }}
      />
      <Box sx={{ pt: 1 }}>
        <LabelColorSwatches
          value={labelColor}
          onChange={setLabelColor}
          disabled={create.isPending}
        />
      </Box>
      <Button
        type="submit"
        variant="contained"
        startIcon={<AddRounded />}
        disabled={create.isPending || (touched && Boolean(error))}
      >
        {t("settings.labels.add")}
      </Button>
    </Box>
  );
};

const LabelTable: React.FC<{
  labels: DocumentCraftingLabel[];
  editable: boolean;
  project: boolean;
  emptyText: string;
}> = ({ labels, editable, project, emptyText }) => {
  const { t } = useTranslation("documentCrafting");
  if (!labels.length) {
    return (
      <Typography sx={{ fontSize: 14, color: color.gray500 }}>{emptyText}</Typography>
    );
  }
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>{t("settings.labels.name")}</TableCell>
            <TableCell sx={headCellSx}>{t("settings.labels.color")}</TableCell>
            {project && (
              <TableCell sx={headCellSx}>{t("settings.labels.usage")}</TableCell>
            )}
            {project && (
              <TableCell sx={headCellSx}>{t("settings.labels.archived")}</TableCell>
            )}
            {editable && (
              <TableCell sx={headCellSx} align="right">
                {t("settings.labels.actions")}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {labels.map((label) => (
            <LabelRow
              key={label.id}
              label={label}
              siblings={labels}
              editable={editable}
              project={project}
            />
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

/**
 * Project labels (workspace admins and platform admins manage them; everyone
 * else reads them; not shown without a workspace) and the user's own personal
 * labels.
 */
const LabelSettings: React.FC<{
  canManageWorkspace: boolean;
  hasWorkspace?: boolean;
}> = ({ canManageWorkspace, hasWorkspace = true }) => {
  const { t } = useTranslation("documentCrafting");
  const [showArchived, setShowArchived] = useState(false);
  const labels = useDocumentCraftingLabels(showArchived);
  const project = labels.data?.project ?? [];
  const personal = labels.data?.personal ?? [];

  const body = (content: React.ReactNode) =>
    labels.data ? (
      content
    ) : labels.isError ? (
      <ErrorState onRetry={() => labels.refetch()} />
    ) : (
      <LoadingState />
    );

  const archivedSwitch = (
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={showArchived}
          onChange={(event) => setShowArchived(event.target.checked)}
        />
      }
      label={
        <Typography sx={{ fontSize: 13, color: color.gray700 }}>
          {t("settings.labels.showArchived")}
        </Typography>
      }
      sx={{ mr: 0, flexShrink: 0 }}
    />
  );

  return (
    <>
      {hasWorkspace && (
        <Panel
          title={t("settings.labels.projectTitle")}
          subtitle={t("settings.labels.projectSubtitle")}
          action={archivedSwitch}
        >
          {!canManageWorkspace && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("settings.labels.readOnly")}
            </Alert>
          )}
          {body(
            <>
              {canManageWorkspace && (
                <NewLabelForm scope={DocumentCraftingLabelScope.PROJECT} existing={project} />
              )}
              <LabelTable
                labels={project}
                editable={canManageWorkspace}
                project
                emptyText={t("settings.labels.empty")}
              />
            </>,
          )}
        </Panel>
      )}
      <Panel
        title={t("settings.labels.personalTitle")}
        subtitle={t("settings.labels.personalSubtitle")}
        action={hasWorkspace ? undefined : archivedSwitch}
      >
        {body(
          <>
            <NewLabelForm scope={DocumentCraftingLabelScope.PERSONAL} existing={personal} />
            <LabelTable
              labels={personal}
              editable
              project={false}
              emptyText={t("settings.labels.emptyPersonal")}
            />
          </>,
        )}
      </Panel>
    </>
  );
};

export default LabelSettings;
